<template>
	<!-- Balance and main content -->
	<div class="w-full md:w-3/4 lg:w-4/5 h-full flex flex-col items-start pair-bg overflow-y-auto custom-scrollbar md:rounded-tr-2xl md:rounded-bl-none md:rounded-tl-none rounded-b-2xl z-10">
		<div class="flex items-center justify-between w-full px-4 md:pt-12 md:px-8">
			<div class="flex items-center justify-center ml-4 md:ml-0">
				<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkMainReceive : lightMainReceive" alt="Receive Icon" class="w-6 h-6 md:w-8 md:h-8 mr-3"/>
				<h1 class="text-2xl md:text-4xl font-archivomedium text-[var(--text-color)]">
					Receive
				</h1>
				<img :src="selectedChain.icon" alt="Chain Icon" class="w-8 h-8 md:w-10 md:h-10 mb-4 ml-2 mt-4 cursor-pointer md:top-16 md:right-16"/>
				<button @click="$emit('backToOverview')" class="hidden md:block absolute top-8 right-8">
					<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkX : lightX" alt="Close Icon" class="w-3 h-3"/>
				</button>
			</div>
			<!-- back button -->
			<button @click="$emit('backToOverview')" class="block md:hidden ml-auto md:mt-2 mr-4 flex items-center space-x-1 px-3 py-2 text-sm font-archivoregular text-[var(--secondary-text-color)] bg-[var(--accent-color-50)] hover:bg-[var(--accent-color-30)] rounded-full transition-colors">
				<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
				</svg>
				<span class="hover:text-[var(--secondary-text-color)] text-xs">Back</span>
			</button>
		</div>

		<div class="my-2 mx-auto w-full md:w-[80%] lg:w-[65%] xl:w-[50%] p-4">
			<p class="mb-4 text-center font-archivolight text-[var(--secondary-text-color)]">
				Scan the QR code to view your {{ selectedAssetObject.symbol }} address.
			</p>
			<!-- Only render QR when we actually have a nonempty walletAddress -->
			<div v-if="walletAddress" class="mb-4 flex justify-center">
				<QRCodeVue :value="walletAddress" :size="250" level="H" class="bg-[#FFFFFF] p-2 rounded-sm" :background="'#FFFFFF'"></QRCodeVue>
			</div>
			<div v-else class="mb-4 flex justify-center">
				<!-- Optionally, show a spinner or placeholder while the address is loading -->
				<div class="loader"></div>
			</div>
			<div @click="$emit('copyWalletAddress')" class="my-4 text-balance flex flex-wrap items-center justify-center">
				<p class="w-full text-center mt-2 text-[var(--secondary-text-color)] text-sm break-all">{{ walletAddress }}</p>
				<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkCopy : lightCopy" alt="Copy Icon" class="w-5 h-5 ml-2 cursor-pointer"/>
			</div>
			<button  @click="$emit('copyWalletAddress')" class="mt-4 font-archivoregular w-full bg-transparent text-[var(--accent-color)] border border-[var(--accent-color)] py-2 rounded-full hover:bg-[var(--accent-color)] hover:text-[var(--hover-text-color)] transition duration-100 shadow-inner">
				Copy
			</button>
			<button  @click="$emit('backToOverview')" class="mt-4 font-archivoregular w-full bg-transparent text-[var(--accent-color)] border border-[var(--accent-color)] py-2 rounded-full hover:bg-[var(--accent-color)] hover:text-[var(--hover-text-color)] transition duration-100 shadow-inner">
				Back
			</button>
				<!-- New dynamic info block on Receive page -->
			<div class="info-block flex items-center space-x-2 px-4 py-2 md:py-1 rounded-lg bg-blue-100 border border-blue-300 mt-8">
				<!-- Aesthetic info icon (using an SVG) -->
				<svg class="md:w-16 md:h-16 w-20 h-20 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 20a8 8 0 100-16 8 8 0 000 16z" />
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01" />
				</svg>
				<p class="text-sm text-blue-800 text-center py-1">
					This address can only accept assets on the {{ selectedChain.name }} network. Sending any other types of tokens, on any other networks, to this address may result in permanent loss.
				</p>
			</div>
		</div>
	</div>
</template>

<script setup>
	import QRCodeVue from 'qrcode.vue'
	import lightMainReceive from '../assets/ReceiveIconLightTheme.png'
	import darkMainReceive from '../assets/ReceiveIconDarkTheme.png'
	import lightX from '../assets/X.png'
	import darkX from '../assets/XWhite.png'
	import lightCopy from '../assets/LightCopyIcon.png'
	import darkCopy from '../assets/DarkCopyIcon.png'

	const props = defineProps({
		currentTheme: { type: String, required: true },
		selectedChain:  { type: Object, required: true },
		selectedAssetObject: { type: Object, required: true },
		walletAddress: { type: String, required: true },
	})

	const emit = defineEmits([
		'backToOverview',
		'copyWalletAddress'
	])
</script>

<style scoped>
	.pair-bg { background: var(--main-bg-color); }
	.balance-bg {
		background: var(--balance-bg-color);
	}

	/* custom scrollbar */
	.custom-scrollbar {
		overflow-y: auto;
		scrollbar-width: thin;
		scrollbar-color: #888 #f1f1f1;
	}
	.custom-scrollbar::-webkit-scrollbar {
		width: 8px;
	}
	.custom-scrollbar::-webkit-scrollbar-track {
		background: #f1f1f1;
		border-radius: 16px;
		margin: 5% 0;
	}
	.custom-scrollbar::-webkit-scrollbar-thumb {
		background-color: #888;
		border-radius: 8px;
	}
	.custom-scrollbar::-webkit-scrollbar-thumb:hover {
		background-color: #555;
	}

	/* fade-in / fade-out transitions */
	.fade-enter-active,
	.fade-leave-active {
		transition: opacity 0.2s ease-in-out;
	}
	.fade-enter-from,
	.fade-leave-to {
		opacity: 0;
	}

	.loader {
		border: 8px solid #f3f3f3;
		border-top: 8px solid var(--accent-color);
		border-radius: 50%;
		width: 28px;
		height: 28px;
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}
</style>
